"use client";

import "@/components/home/brick-breaker.css";
import { FigureCaptionLine } from "@/components/home/figure-caption";
import { SectionKicker } from "@/components/home/section-kicker";

import { playSound } from "@/components/ui/sensory-ui/config/engine";
import { createSoundFromTune } from "@/components/ui/sensory-ui/sounds/core/factory";
import { CRISP_INSTRUMENT } from "@/components/ui/sensory-ui/sounds/core/instruments";
import { HERO_TUNES, INTERACTION_TUNES, NOTIFICATION_TUNES } from "@/components/ui/sensory-ui/sounds/core/tunes";
import { useDisputeWellLogoUrls } from "@/components/ui/molecules/OrgLogo";
import { routes } from "@/lib/routing/routes";
import { heroSerif } from "@/lib/theme/hero-serif";
import Link from "next/link";
import { memo, useEffect, useRef, useState, useSyncExternalStore } from "react";

const DESKTOP_FIELD = {
  W: 960,
  H: 540,
  PADDLE_W: 120,
  BRICK_COLS: 10,
  BALL_SPEED: 380,
};
const PHONE_FIELD = {
  W: 420,
  H: 620,
  PADDLE_W: 104,
  BRICK_COLS: 6,
  BALL_SPEED: 300,
};
const PADDLE_H = 12;
const BALL_R = 7;
const BRICK_ROWS = 5;
const BRICK_GAP = 8;
const BRICK_H = 22;
const START_SCORE = 527;
const WIN_SCORE = 850;
const PADDLE_FOLLOW = 14;
const MAX_DT = 0.032;

const ROW_COLORS = ["#7ee0a3", "#a3e635", "#eab308", "#f59e0b", "#ef4444"];

const COARSE_POINTER_QUERY = "(pointer: coarse)";
const PHONE_FIELD_QUERY = "(max-width: 639px)";

// matchMedia is the external store for both of these; read it through
// useSyncExternalStore rather than mirroring it into state from an effect.
function mediaQuerySubscriber(query: string) {
  return (onStoreChange: () => void) => {
    const mq = window.matchMedia(query);
    mq.addEventListener("change", onStoreChange);
    return () => mq.removeEventListener("change", onStoreChange);
  };
}

const subscribeCoarsePointer = mediaQuerySubscriber(COARSE_POINTER_QUERY);
const subscribePhoneField = mediaQuerySubscriber(PHONE_FIELD_QUERY);

function matchesCoarsePointer() {
  return window.matchMedia(COARSE_POINTER_QUERY).matches;
}

function matchesPhoneField() {
  return window.matchMedia(PHONE_FIELD_QUERY).matches;
}

function noCoarsePointerOnServer() {
  return false;
}

// null = "field size not measured yet"; the canvas setup effect waits for it.
function noPhoneFieldOnServer() {
  return null;
}

const BRICK_TICKS = ROW_COLORS.map((_, row) =>
  createSoundFromTune(INTERACTION_TUNES.tap, {
    ...CRISP_INSTRUMENT,
    pitchMult: 1.6 - row * 0.18,
  }),
);
const PADDLE_TICK = createSoundFromTune(
  INTERACTION_TUNES.subtle,
  CRISP_INSTRUMENT,
);
const WIN_SOUND = createSoundFromTune(HERO_TUNES.complete, CRISP_INSTRUMENT);
const LOSE_SOUND = createSoundFromTune(
  NOTIFICATION_TUNES.error,
  CRISP_INSTRUMENT,
);

type Phase = "idle" | "playing" | "paused" | "won" | "lost";

type Brick = {
  x: number;
  y: number;
  w: number;
  h: number;
  row: number;
  alive: boolean;
  alpha: number;
};

function buildBricks(W: number, BRICK_COLS: number): Brick[] {
  const bricks: Brick[] = [];
  const wallW = W - BRICK_GAP * (BRICK_COLS + 1);
  const brickW = wallW / BRICK_COLS;

  for (let row = 0; row < BRICK_ROWS; row++) {
    for (let col = 0; col < BRICK_COLS; col++) {
      bricks.push({
        x: BRICK_GAP + col * (brickW + BRICK_GAP),
        y: 64 + row * (BRICK_H + BRICK_GAP),
        w: brickW,
        h: BRICK_H,
        row,
        alive: true,
        alpha: 1,
      });
    }
  }

  return bricks;
}

export const BrickBreaker = memo(function BrickBreaker() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { light: logoUrl } = useDisputeWellLogoUrls();
  const [phase, setPhase] = useState<Phase>("idle");
  const coarsePointer = useSyncExternalStore(
    subscribeCoarsePointer,
    matchesCoarsePointer,
    noCoarsePointerOnServer,
  );
  const phoneField = useSyncExternalStore<boolean | null>(
    subscribePhoneField,
    matchesPhoneField,
    noPhoneFieldOnServer,
  );

  // A field-size change tears the game down and rebuilds it, so the phase goes
  // back to idle. Adjusted during render off the previous field rather than
  // from the setup effect below, which would cascade a second render.
  const [lastPhoneField, setLastPhoneField] = useState(phoneField);
  if (lastPhoneField !== phoneField) {
    setLastPhoneField(phoneField);
    setPhase("idle");
  }

  // The rAF loop and the pointer/key handlers are registered once and must read
  // the CURRENT phase without being torn down and re-subscribed on every phase
  // change, so phase is mirrored into a ref. Written in an effect, not during
  // render: a render can be discarded (StrictMode double-invoke, a concurrent
  // render that never commits) and a ref mutated there would keep the value
  // from a render that never happened. Every reader is deferred — the step
  // loop, start(), onPointerMove, onKeyDown — so post-commit timing is soon
  // enough for all of them.
  const phaseRef = useRef<Phase>("idle");
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    if (phoneField === null) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const field = phoneField ? PHONE_FIELD : DESKTOP_FIELD;
    const { W, H, PADDLE_W, BRICK_COLS, BALL_SPEED } = field;
    canvas.style.aspectRatio = `${W} / ${H}`;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    let bricks = buildBricks(W, BRICK_COLS);
    let paddleX = W / 2 - PADDLE_W / 2;
    let paddleTargetX = paddleX;
    let ballX = W / 2;
    let ballY = H - 80;
    let dirX = 0.55;
    let dirY = -0.84;
    let lives = 3;
    let score = START_SCORE;
    let displayScore = START_SCORE;
    let lastTime = 0;
    let raf = 0;
    let inView = true;

    const scorePerBrick = Math.floor(
      (WIN_SCORE - START_SCORE) / (BRICK_COLS * BRICK_ROWS),
    );

    const logo = new window.Image();
    logo.src = logoUrl;

    const resetBall = () => {
      ballX = paddleX + PADDLE_W / 2;
      ballY = H - 80;
      const angle = (Math.random() * 0.6 - 0.3) * Math.PI;
      dirX = Math.sin(angle) || 0.3;
      dirY = -Math.abs(Math.cos(angle));
    };

    const restart = () => {
      bricks = buildBricks(W, BRICK_COLS);
      lives = 3;
      score = START_SCORE;
      displayScore = START_SCORE;
      resetBall();
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      if (logo.complete && logo.naturalWidth > 0) {
        const logoW = 300;
        const logoH = (logo.naturalHeight / logo.naturalWidth) * logoW;
        ctx.globalAlpha = 0.06;
        ctx.drawImage(
          logo,
          W / 2 - logoW / 2,
          H / 2 - logoH / 2 + 40,
          logoW,
          logoH,
        );
        ctx.globalAlpha = 1;
      }

      ctx.font = "600 15px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.fillStyle = "rgb(28 28 26 / 45%)";
      ctx.textAlign = "left";
      ctx.fillText("SCORE", BRICK_GAP, 32);
      ctx.fillStyle = score >= WIN_SCORE ? "#15803d" : "#1c1c1a";
      ctx.font = "600 24px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.fillText(
        String(Math.min(Math.round(displayScore), WIN_SCORE)),
        BRICK_GAP + 66,
        33,
      );
      ctx.textAlign = "right";
      ctx.fillStyle = "rgb(28 28 26 / 45%)";
      ctx.font = "600 15px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.fillText("●".repeat(lives).padEnd(3, "○"), W - BRICK_GAP, 32);

      for (const brick of bricks) {
        if (brick.alpha <= 0) {
          continue;
        }
        ctx.globalAlpha = brick.alpha;
        ctx.fillStyle = ROW_COLORS[brick.row] ?? "#ef4444";
        const inset = (1 - brick.alpha) * 8;
        ctx.fillRect(
          brick.x + inset,
          brick.y + inset,
          brick.w - inset * 2,
          brick.h - inset * 2,
        );
      }
      ctx.globalAlpha = 1;

      ctx.fillStyle = "#1c1c1a";
      ctx.fillRect(paddleX, H - 36, PADDLE_W, PADDLE_H);
      ctx.beginPath();
      ctx.arc(ballX, ballY, BALL_R, 0, Math.PI * 2);
      ctx.fill();
    };

    const step = (time: number) => {
      const dt = Math.min((time - lastTime) / 1000 || 0, MAX_DT);
      lastTime = time;

      for (const brick of bricks) {
        if (!brick.alive && brick.alpha > 0) {
          brick.alpha = Math.max(0, brick.alpha - dt * 5);
        }
      }
      displayScore += (score - displayScore) * Math.min(1, dt * 8);

      if (phaseRef.current !== "playing") {
        draw();
        return;
      }

      paddleX += (paddleTargetX - paddleX) * Math.min(1, dt * PADDLE_FOLLOW);
      ballX += dirX * BALL_SPEED * dt;
      ballY += dirY * BALL_SPEED * dt;

      if (ballX < BALL_R) {
        ballX = BALL_R;
        dirX = Math.abs(dirX);
      }
      if (ballX > W - BALL_R) {
        ballX = W - BALL_R;
        dirX = -Math.abs(dirX);
      }
      if (ballY < BALL_R) {
        ballY = BALL_R;
        dirY = Math.abs(dirY);
      }

      if (
        dirY > 0 &&
        ballY > H - 36 - BALL_R &&
        ballY < H - 36 + PADDLE_H &&
        ballX > paddleX - BALL_R &&
        ballX < paddleX + PADDLE_W + BALL_R
      ) {
        const hit = (ballX - (paddleX + PADDLE_W / 2)) / (PADDLE_W / 2);
        const angle = hit * (Math.PI / 3);
        dirX = Math.sin(angle);
        dirY = -Math.abs(Math.cos(angle));
        void playSound(PADDLE_TICK, { volume: 0.35 });
      }

      for (const brick of bricks) {
        if (!brick.alive) {
          continue;
        }
        if (
          ballX > brick.x - BALL_R &&
          ballX < brick.x + brick.w + BALL_R &&
          ballY > brick.y - BALL_R &&
          ballY < brick.y + brick.h + BALL_R
        ) {
          brick.alive = false;
          score += scorePerBrick;
          void playSound(BRICK_TICKS[brick.row] ?? BRICK_TICKS[0], {
            volume: 0.5,
          });
          const fromLeft = Math.abs(ballX - brick.x);
          const fromRight = Math.abs(brick.x + brick.w - ballX);
          const fromTop = Math.abs(ballY - brick.y);
          const fromBottom = Math.abs(brick.y + brick.h - ballY);
          const min = Math.min(fromLeft, fromRight, fromTop, fromBottom);
          if (min === fromTop) {
            dirY = -Math.abs(dirY);
          } else if (min === fromBottom) {
            dirY = Math.abs(dirY);
          } else if (min === fromLeft) {
            dirX = -Math.abs(dirX);
          } else {
            dirX = Math.abs(dirX);
          }
          break;
        }
      }

      if (bricks.every((brick) => !brick.alive)) {
        score = WIN_SCORE;
        displayScore = WIN_SCORE;
        setPhase("won");
        void playSound(WIN_SOUND, { volume: 0.5 });
      }

      if (ballY > H + BALL_R) {
        lives -= 1;
        if (lives <= 0) {
          setPhase("lost");
          void playSound(LOSE_SOUND, { volume: 0.4 });
        } else {
          resetBall();
        }
      }

      draw();
      raf = requestAnimationFrame(step);
    };

    const start = () => {
      if (phaseRef.current === "playing") {
        return;
      }
      if (phaseRef.current === "lost" || phaseRef.current === "won") {
        restart();
      }
      setPhase("playing");
      cancelAnimationFrame(raf);
      lastTime = 0;
      raf = requestAnimationFrame((time) => {
        lastTime = time;
        raf = requestAnimationFrame(step);
      });
    };

    const onPointerMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * W;
      paddleTargetX = Math.max(0, Math.min(W - PADDLE_W, x - PADDLE_W / 2));
      if (phaseRef.current !== "playing") {
        paddleX = paddleTargetX;
        draw();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Space" || !inView) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable)) {
        return;
      }
      event.preventDefault();
      if (phaseRef.current === "playing") {
        setPhase("paused");
      } else {
        start();
      }
    };

    const observer = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
    });
    observer.observe(canvas);

    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("click", start);
    window.addEventListener("keydown", onKeyDown);
    draw();

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("click", start);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [phoneField, logoUrl]);

  return (
    <section
      className={`dw-home-game ${heroSerif.variable}`}
      aria-label="Brick breaker" data-component="BrickBreaker" data-location="src/components/home/brick-breaker.tsx">
      <div className="dw-home__measure dw-home__stack">
        <SectionKicker
          numeral="08"
          label="Free Time"
          note={<>You&apos;ll have plenty of it soon</>}
        />
        <h2 className="dw-home__headline dw-text-hero" data-tone="foreground">
          You&apos;ve done a lot of reading. Take a break.
        </h2>
        <figure className="dw-home-game__figure">
          <FigureCaptionLine>
            Fig. 11 · Knock every negative item off the report. Start at 527.
            There&apos;s an 850 in it for you.
          </FigureCaptionLine>
          <div className="dw-home-game__card">
            <canvas
              ref={canvasRef}
              className="dw-home-game__canvas"
              style={{ aspectRatio: `${DESKTOP_FIELD.W} / ${DESKTOP_FIELD.H}` }}
            />
            {phase !== "playing" && (
              <div className="dw-home-game__overlay">
                {phase === "idle" && (
                  <p className="dw-text-body dw-tabular-nums" data-tone="foreground">
                    {coarsePointer
                      ? "[ tap to play ]"
                      : "[ click or press space to play ]"}
                  </p>
                )}
                {phase === "paused" && (
                  <p className="dw-text-body dw-tabular-nums" data-tone="foreground">
                    {coarsePointer
                      ? "[ paused — tap to resume ]"
                      : "[ paused — space to resume ]"}
                  </p>
                )}
                {phase === "lost" && (
                  <p className="dw-text-body dw-tabular-nums" data-tone="foreground">
                    the bureaus won this round.{" "}
                    {coarsePointer
                      ? "[ tap to retry ]"
                      : "[ click or press space to retry ]"}
                  </p>
                )}
                {phase === "won" && (
                  <div className="dw-home-game__overlay-stack">
                    <p className="dw-text-kpi" data-tone="foreground">
                      850. Perfect score.
                    </p>
                    <p className="dw-text-body dw-tabular-nums" data-tone="foreground">
                      imagine if software could do that for real.
                    </p>
                    <Link
                      href={routes.crm.signup}
                      className="dw-text-strong dw-tabular-nums"
                    >
                      Start Free Trial
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </figure>
      </div>
    </section>
  );
});
